import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent, trackPageView, trackPaymentInitiated } from '../services/analytics';
import { initializeAfterPayment, preloadAllDomainInsights } from '../services/domainTtsService';
import { isFeatureUnlocked, generatePremiumContent } from '../services/iapService';
import API_URL from '../config/apiConfig';
import { parseJsonSafe, sanitizeErrorMessage } from '../utils/safeJson';
import { useSession } from '../context/SessionContext';
import useDynamicPrices from '../hooks/useDynamicPrices';
import { rlog } from '../utils/railwayLogger';
import { 
  PRODUCT_IDS, 
  getPlatform, 
  isNative, 
  initializeStore, 
  purchaseProduct,
  restorePurchases
} from '../services/iapService';

// Generate premium content based on plan unlocks
const generatePlanContent = async (plan) => {
  const unlocks = plan.unlocks || [];
  const userId = localStorage.getItem('audio_user_id') || localStorage.getItem('session_id');
  const birthRaw = localStorage.getItem('birth_details');
  if (!birthRaw) return;
  
  let birthData;
  try { birthData = JSON.parse(birthRaw); } catch(e) { return; }
  
  const birthPayload = {
    user_id: userId,
    name: birthData.name || 'User',
    year: parseInt(birthData.year),
    month: parseInt(birthData.month),
    day: parseInt(birthData.day),
    hour: parseInt(birthData.hour) || 12,
    minute: parseInt(birthData.minute) || 0,
    place: birthData.place || 'India',
    gender: birthData.gender || 'unknown',
    latitude: parseFloat(birthData.latitude) || 28.6139,
    longitude: parseFloat(birthData.longitude) || 77.209
  };
  const now = new Date();
  
  const tasks = [];
  
  // All plans: Generate AI Life Insights + Enhanced Insights
  if (unlocks.includes('life_insights')) {
    // Mark as in-progress so DomainInsights doesn't duplicate this call
    localStorage.setItem('ai_generation_in_progress', 'true');
    tasks.push(
      fetch(`${API_URL}/api/ai-story/life-insights-fast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          birth_data: birthPayload,
          language: 'english',
          year: now.getFullYear()
        })
      }).then(r => r.json()).then(data => {
        if (data.success && data.life_insights) {
          const cachePayload = {
            ai_generated: true,
            life_insights: data.life_insights,
            language: 'english'
          };
          // Save under BOTH keys so DomainInsights finds it instantly
          localStorage.setItem('ai_content_data', JSON.stringify(cachePayload));
          localStorage.setItem('ai_content_english', JSON.stringify(cachePayload));
          console.log('AI Life Insights generated after payment (english)');
          
          // Mark generation complete so DomainInsights doesn't re-trigger
          localStorage.setItem('ai_generation_in_progress', 'false');
        }
      }).catch(e => {
        console.log('AI content error:', e.message);
        localStorage.setItem('ai_generation_in_progress', 'false');
      }),
      
      fetch(`${API_URL}/api/dashboard/enhanced-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...birthPayload, force_refresh: true })
      }).then(r => r.json()).then(data => {
        if (data.success && data.insights) {
          localStorage.setItem('domain_insights_cache', JSON.stringify(data.insights?.enhanced_domains || data.insights));
          console.log('Enhanced insights generated after payment');
        }
      }).catch(e => console.log('Enhanced insights error:', e.message))
    );
  }
  
  // ₹999: Generate Timeline, Monthly, Yearly
  if (unlocks.includes('timeline')) {
    tasks.push(
      fetch(`${API_URL}/api/timeline/monthly-detailed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...birthPayload, person_id: userId, target_month: now.getMonth() + 1, target_year: now.getFullYear() })
      }).then(r => r.json()).then(data => {
        if (data.success) {
          localStorage.setItem('detailed_monthly_data', JSON.stringify({ data: data.data, month: now.getMonth(), year: now.getFullYear(), timestamp: Date.now() }));
          console.log('Monthly predictions generated after payment');
        }
      }).catch(e => console.log('Monthly error:', e.message)),
      
      fetch(`${API_URL}/api/timeline/yearly-detailed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...birthPayload, person_id: userId, target_year: now.getFullYear() })
      }).then(r => r.json()).then(data => {
        if (data.success) {
          localStorage.setItem('detailed_yearly_report', JSON.stringify({ data: data.data || data, year: now.getFullYear(), timestamp: Date.now() }));
          console.log('Yearly report generated after payment');
        }
      }).catch(e => console.log('Yearly error:', e.message))
    );
  }
  
  // Execute all tasks in parallel
  await Promise.allSettled(tasks);
  console.log('Post-payment content generation complete');
};

const PaymentPlans = () => {
  const navigate = useNavigate();
  const { updatePremiumStatus, user, isAuthenticated, isLoading, isPremium, refreshSubscription, pollSubscriptionUntilPremium } = useSession();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [platform, setPlatform] = useState('web');
  const [upgradePrices, setUpgradePrices] = useState({}); // {starter: {upgrade_price, is_upgrade, savings}, ...}
  const [restoring, setRestoring] = useState(false);
  const { packages: livePackages, getDisplayPrice, format: formatPrice } = useDynamicPrices();

  // Read URL params for contextual filtering
  const urlParams = new URLSearchParams(window.location.search);
  const minPlanPrice = parseInt(urlParams.get('min') || '0');
  const source = urlParams.get('source') || '';

  // Detect current plan level from session (not just localStorage to avoid stale data)
  const currentPlanLevel = (() => {
    // If session says not premium, user is free — ignore stale localStorage
    if (!isPremium && !isLoading) return 0;
    try {
      const unlocks = JSON.parse(localStorage.getItem('plan_unlocks') || '[]');
      if (unlocks.includes('timeline')) return 3; // Ultimate
      if (unlocks.includes('future_reels')) return 2; // Premium
      if (unlocks.includes('life_insights') || unlocks.includes('sage_questions')) return 1; // Starter
    } catch {}
    return 0; // Free
  })();

  const currentPlanName = ['Free', 'Starter', 'Premium', 'Ultimate'][currentPlanLevel];

  useEffect(() => {
    if (isLoading) return;
    trackPageView('payment_plans');
    trackEvent('payment_page_view');
    // Allow device-based users (from onboarding) to access payment
    const hasDeviceId = localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id');
    const hasBirthDetails = localStorage.getItem('birthDetails');
    if (!isAuthenticated && !hasDeviceId && !hasBirthDetails) {
      toast.error('Please complete onboarding first');
      navigate('/onboarding-new');
    }
    
    // Initialize IAP store and detect platform
    const init = async () => {
      const currentPlatform = getPlatform();
      setPlatform(currentPlatform);
      
      if (isNative()) {
        await initializeStore();
      }
      
      // Fetch upgrade prices from backend — always attempt, backend returns
      // full_price=upgrade_price when user has no active sub. Don't gate on
      // isPremium flag (it might still be loading from session).
      const rawId = user?.user_id || localStorage.getItem('user_id') || localStorage.getItem('session_id') || localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id');
      // Only query upgrade-prices if we have a REAL authenticated user_id.
      // Ghost IDs (device_id / session_id) return full prices anyway and
      // waste a round-trip.
      const userId = (rawId && !String(rawId).startsWith('native-') && !String(rawId).startsWith('web-') && !String(rawId).startsWith('session_')) ? rawId : null;
      if (userId) {
        try {
          const res = await fetch(`${API_URL}/api/razorpay/upgrade-prices/${userId}`);
          const data = await res.json();
          if (data.success) {
            setUpgradePrices(data.prices || {});
            rlog('upgrade_prices_fetched', {
              user_id: userId,
              prices: data.prices,
              current_plan: data.current_plan,
              current_plan_price: data.current_plan_price,
              days_used: data.days_used,
              days_remaining: data.days_remaining,
            });
          } else {
            rlog('upgrade_prices_failed', { user_id: userId, message: data.message });
          }
        } catch (e) {
          console.log('Upgrade prices fetch error:', e);
          rlog('upgrade_prices_exception', { user_id: userId, error: e?.message });
        }
      }
    };
    init();
    
    // Check if returning from native payment (Chrome Custom Tab)
    const checkPendingPayment = async () => {
      const pendingLinkId = localStorage.getItem('pending_payment_link_id');
      const pendingTime = localStorage.getItem('pending_payment_time');
      if (!pendingLinkId || !pendingTime) return;
      
      // Only check if payment was initiated less than 30 minutes ago
      const elapsed = Date.now() - parseInt(pendingTime);
      if (elapsed > 30 * 60 * 1000) {
        localStorage.removeItem('pending_payment_link_id');
        localStorage.removeItem('pending_payment_package');
        localStorage.removeItem('pending_payment_time');
        return;
      }
      
      console.log('[Payment] Checking pending payment status for link:', pendingLinkId);
      try {
        const userId = localStorage.getItem('user_id') || localStorage.getItem('firebase_uid') || '';
        const res = await fetch(`${API_URL}/api/razorpay/subscription/${userId}`);
        const subData = await res.json();
        
        if (subData.is_premium) {
          console.log('[Payment] Payment completed! User is now premium.');
          localStorage.removeItem('pending_payment_link_id');
          localStorage.removeItem('pending_payment_package');
          localStorage.removeItem('pending_payment_time');
          
          // Store subscription locally
          localStorage.setItem('subscription_status', 'active');
          localStorage.setItem('is_premium', 'true');
          if (subData.unlocks) localStorage.setItem('unlocked_features', JSON.stringify(subData.unlocks));
          if (subData.sage_credits) {
            localStorage.setItem('sage_credits', String(subData.sage_credits));
            window.dispatchEvent(new CustomEvent('credits-updated'));
          }
          
          toast.success('Payment successful! Premium features unlocked.');
          
          // Trigger post-payment content generation
          const pkg = localStorage.getItem('pending_payment_package') || 'starter';
          const plan = plans.find(p => p.id?.includes(pkg)) || plans[0];
          generatePremiumContent(plan.unlocks || ['life_insights']);
          
          navigate('/dashboard');
        }
      } catch (e) {
        console.log('[Payment] Pending payment check failed:', e.message);
      }
    };
    checkPendingPayment();
    
    // Also listen for app resume (in case user switched apps during payment)
    const handleAppResume = () => {
      console.log('[Payment] App resumed — checking pending payment...');
      setTimeout(checkPendingPayment, 1500);
    };
    document.addEventListener('resume', handleAppResume);
    
    // Handle redirect from Android/iOS app — auto-trigger payment for specified package
    const urlParams = new URLSearchParams(window.location.search);
    const redirectPackage = urlParams.get('package');
    const redirectUserId = urlParams.get('user_id');
    if (redirectPackage && urlParams.get('redirect') === 'true') {
      const planMap = { 'starter': 0, 'premium': 1, 'ultimate': 2 };
      const planIndex = planMap[redirectPackage];
      if (planIndex !== undefined && plans[planIndex]) {
        if (redirectUserId) localStorage.setItem('redirect_user_id', redirectUserId);
        setTimeout(() => { handlePayment(plans[planIndex]); }, 1000);
      }
    }
    
    return () => {
      document.removeEventListener('resume', handleAppResume);
    };
  }, [isAuthenticated, isLoading, isPremium, user?.user_id, navigate]);

  const plans = [
    {
      id: 'starter_199',
      productId: PRODUCT_IDS.STARTER_199,
      name: 'Starter',
      price: 199,
      originalPrice: 399,
      duration: '/month',
      tag: '',
      level: 1,
      features: [
        'Life Insights Unlocked',
        '300 Sage Coins',
        '3 Ask Me Questions',
        'Past Story Narration',
      ],
      notIncluded: ['Future Reels', 'Timeline Access'],
      credits: 300,
      popular: false,
    },
    {
      id: 'premium_599',
      productId: PRODUCT_IDS.PREMIUM_599,
      name: 'Premium',
      price: 599,
      originalPrice: 999,
      duration: '/month',
      tag: 'MOST POPULAR',
      level: 2,
      features: [
        'Life Insights Unlocked',
        '300 Sage Coins',
        '3 Ask Me Questions',
        'All 8 Future Reels',
        'Past Story Narration',
      ],
      notIncluded: ['Timeline Access'],
      credits: 300,
      popular: true,
    },
    {
      id: 'ultimate_999',
      productId: PRODUCT_IDS.ULTIMATE_999,
      name: 'Ultimate',
      price: 999,
      originalPrice: 1999,
      duration: '/month',
      tag: 'EVERYTHING',
      level: 3,
      features: [
        'Life Insights Unlocked',
        '300 Sage Coins',
        '3 Ask Me Questions',
        'All 8 Future Reels',
        'Full Timeline Access',
        'Monthly & Yearly Predictions',
      ],
      notIncluded: [],
      credits: 300,
      popular: false,
    }
  ];

  // Filter: only show plans ABOVE current plan level AND at/above min price from URL
  const upgradePlans = plans.filter(p => p.level > currentPlanLevel && p.price >= minPlanPrice);
  const hasMaxPlan = currentPlanLevel >= 3;

  // Dynamic header based on source context
  const headerTitle = hasMaxPlan ? 'You Have The Best Plan' 
    : source === 'timeline' ? 'Unlock Timeline' 
    : source === 'future_reels' ? 'Unlock Future Reels'
    : source === 'upgrade' ? 'Upgrade Your Plan'
    : currentPlanLevel > 0 ? 'Upgrade Your Plan' 
    : 'Choose Your Plan';
  
  const headerSubtext = hasMaxPlan ? 'You have access to all features'
    : source === 'timeline' ? 'Full monthly & yearly predictions await'
    : source === 'future_reels' ? 'See your future across all 8 domains'
    : 'All plans are monthly \u2022 Cancel anytime';

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const userId = user?.user_id || localStorage.getItem('user_id') || '';
      toast.info('Checking for existing purchases...');
      
      // Runs native restorePurchases + backend reconcile
      await restorePurchases(userId);
      
      // Poll subscription endpoint up to 30s for result
      let unlocked = false;
      if (refreshSubscription) {
        unlocked = await refreshSubscription();
      }
      if (!unlocked && pollSubscriptionUntilPremium) {
        unlocked = await pollSubscriptionUntilPremium(30, 3);
      }
      
      if (unlocked) {
        toast.success('Purchase restored! Premium features unlocked.');
        setTimeout(() => navigate('/dashboard'), 1200);
      } else {
        toast.error('No active purchase found. Contact support if you paid recently.');
      }
    } catch (e) {
      console.error('Restore error:', e);
      toast.error('Restore failed. Please try again or contact support.');
    } finally {
      setRestoring(false);
    }
  };

  const handlePayment = async (plan) => {
    setLoading(true);
    setSelectedPlan(plan.id);
    trackPaymentInitiated(plan.name, plan.price);
    rlog('payment_plan_clicked', {
      plan_id: plan.id,
      plan_name: plan.name,
      display_price: plan.price,
      product_id: plan.productId,
      current_plan_level: currentPlanLevel,
      current_plan: localStorage.getItem('plan_id'),
      is_upgrade: currentPlanLevel > 0 && plan.level > currentPlanLevel,
      upgrade_price_ui: upgradePrices[plan.id]?.upgrade_price,
      full_price_ui: plan.price,
    });
    
    try {
      const userId = user?.user_id || localStorage.getItem('user_id') || localStorage.getItem('session_id') || localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id');
      
      if (!userId) {
        toast.error('Please complete onboarding first');
        navigate('/onboarding-new');
        return;
      }
      
      // Mock payment for Emergent demo flow (/watch1)
      const isEmergentDemo = localStorage.getItem('demo_source') === 'emergent';
      let paymentResult;
      
      if (isEmergentDemo) {
        // Instant mock for demo — no delay
        const unlockMap = {
          'starter_199': ['deep_dive', 'life_insights', 'sage_questions'],
          'premium_599': ['deep_dive', 'life_insights', 'sage_questions', 'future_reels'],
          'ultimate_999': ['deep_dive', 'life_insights', 'sage_questions', 'future_reels', 'timeline', 'sage_unlimited']
        };
        paymentResult = {
          success: true,
          transaction_id: 'demo_' + Date.now(),
          payment_id: 'demo_pay_' + Date.now(),
          subscription_expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          unlocks: unlockMap[plan.id] || ['deep_dive', 'life_insights', 'sage_questions', 'future_reels', 'timeline', 'sage_unlimited']
        };
      } else {
        // Step 1: Use Razorpay SDK popup (works on all platforms — web + native)
        try {
          const packageMap = {
            'starter_199': 'starter', 'premium_599': 'premium', 'ultimate_999': 'ultimate',
          };
          const backendPkgId = packageMap[plan.id] || plan.id;
          
          console.log('[Payment] Opening Razorpay SDK checkout for:', backendPkgId);
          
          // Create order on backend (differential pricing handled server-side)
          const orderRes = await fetch(`${API_URL}/api/razorpay/create-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              package_id: backendPkgId,
              user_id: userId,
              email: localStorage.getItem('user_email') || '',
              name: localStorage.getItem('user_name') || 'User',
              phone: localStorage.getItem('user_phone') || ''
            })
          });
          const orderParsed = await parseJsonSafe(orderRes);
          if (!orderParsed.ok || !orderParsed.data) {
            paymentResult = { success: false, message: orderParsed.error || 'Could not reach payment server. Please retry.' };
          } else {
          const orderData = orderParsed.data;
          
          if (orderData.mock) {
            // Backend is in mock mode — auto-verify inline
            console.log('Mock payment mode — auto-verifying...');
            const verifyRes = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: orderData.order_id,
                razorpay_payment_id: `pay_mock_${Date.now()}`,
                razorpay_signature: 'mock'
              })
            });
            const verifyParsed = await parseJsonSafe(verifyRes);
            const verifyData = verifyParsed.data || {};
            
            if (verifyParsed.ok && verifyData.success) {
              paymentResult = {
                success: true,
                mock: true,
                transaction_id: orderData.order_id,
                payment_id: `pay_mock_${Date.now()}`,
                subscription_expires: verifyData.expires_at || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                unlocks: verifyData.unlocks || plan.unlocks || []
              };
            } else {
              paymentResult = { success: false, message: verifyData.detail || verifyParsed.error || 'Payment verification failed. Please retry.' };
            }
          } else if (orderData.success) {
            // Real payment — open Razorpay SDK popup (in-app, no redirect)
            paymentResult = await purchaseProduct(plan.productId, userId);
          } else {
            paymentResult = { success: false, message: orderData.detail || 'Failed to create payment order' };
          }
          }
        } catch (e) {
          console.log('Payment error:', e.message);
          paymentResult = { success: false, message: sanitizeErrorMessage(e.message, 'Payment service is temporarily unavailable. Please retry.') };
        }
      }
      
      if (paymentResult.success) {
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 30);
        
        const subscriptionData = {
          plan_id: plan.id,
          plan_name: plan.name,
          price: plan.price,
          payment_id: paymentResult.transaction_id || paymentResult.payment_id,
          status: 'active',
          is_recurring: true,
          expires_at: paymentResult.subscription_expires || expiryDate.toISOString(),
          platform: platform,
          credits: plan.credits,
          unlocks: paymentResult.unlocks || []
        };
        
        rlog('payment_success_applied', {
          plan_id: plan.id,
          platform,
          payment_id: paymentResult.transaction_id || paymentResult.payment_id,
          unlocks_count: (paymentResult.unlocks || []).length,
          pre_upgrade_plan: localStorage.getItem('plan_id'),
          pre_upgrade_credits: localStorage.getItem('sage_credits'),
        });
        updatePremiumStatus(true, subscriptionData);
        
        // Only clear caches if upgrading to a new plan (not re-purchasing same tier)
        const existingUnlocks = JSON.parse(localStorage.getItem('plan_unlocks') || '[]');
        const newUnlocks = paymentResult.unlocks || [];
        const isNewContent = newUnlocks.some(u => !existingUnlocks.includes(u));
        
        // Store plan unlocks for feature access checks
        const mergedUnlocks = [...new Set([...existingUnlocks, ...newUnlocks])];
        localStorage.setItem('plan_unlocks', JSON.stringify(mergedUnlocks));
      
        // Smart cache clearing based on WHAT specifically is new
        const newlyUnlockedFeatures = newUnlocks.filter(u => !existingUnlocks.includes(u));
        
        // Only clear AI content caches if life_insights is NEWLY unlocked (fresh user → 199)
        // NOT on upgrade 199→599 where life_insights already existed
        if (newlyUnlockedFeatures.includes('life_insights')) {
          localStorage.removeItem('ai_content_data');
          localStorage.removeItem('ai_content_english');
          localStorage.removeItem('ai_content_hindi');
          localStorage.removeItem('ai_content_hinglish');
          localStorage.removeItem('domain_insights_cache');
        }
        
        // Only clear timeline cache if timeline is NEWLY unlocked (→999)
        if (newlyUnlockedFeatures.includes('timeline')) {
          localStorage.removeItem('health_timeline_data');
        }
        
        // Only clear audio/TTS caches if future_reels is NEWLY unlocked (→599 or →999)
        if (newlyUnlockedFeatures.includes('future_reels')) {
          const domains = ['health', 'career', 'wealth', 'marriage', 'family', 'education', 'travel', 'spiritual'];
          domains.forEach(d => {
            localStorage.removeItem(`domain_audio_cache_${d}`);
          });
          localStorage.removeItem('tts_generation_started');
          localStorage.removeItem('tts_generation_triggered');
          localStorage.removeItem('tts_ultra_fast_complete');
          // CRITICAL: Clear stale progress so HexStory doesn't show old 10% on multiple planets
          localStorage.setItem('domain_progress', '{}');
          localStorage.setItem('audio_ready_domains', '{}');
        }
        
        toast.success(`${plan.name} plan activated! ${plan.credits} Sage Coins added.`);
        
        // CRITICAL: Set credits IMMEDIATELY in localStorage before any event/navigation
        localStorage.setItem('sage_credits', String(plan.credits || 300));
        
        // Dispatch credits update event for dashboard to pick up
        window.dispatchEvent(new CustomEvent('credits-updated'));
        
        // CRITICAL: Set generation flags BEFORE navigating to dashboard
        // This prevents HexStory's auto-regen from firing and creating competing API calls
        if (isNewContent) {
          if (newlyUnlockedFeatures.includes('life_insights')) {
            localStorage.setItem('ai_generation_in_progress', 'true');
          }
          if (newlyUnlockedFeatures.includes('future_reels')) {
            localStorage.setItem('future_reels_generating', 'true');
            localStorage.setItem('future_reels_gen_start', String(Date.now()));
            localStorage.setItem('payment_generation_active', 'true');
            // Lock TTS generation BEFORE navigate — HexStory checks this on mount
            localStorage.setItem('tts_currently_generating', 'true');
          }
        }
        
        // Navigate to dashboard (nav bar preserved) — HexStory loads as default tab
        navigate('/dashboard');
        
        // Background: generate ALL newly unlocked content IN PARALLEL
        // Life Insights (GPT), Timeline (GPT), and Unified Story→TTS (GPT→Edge TTS)
        // are independent streams. Backend semaphore throttles GPT calls automatically.
        // TTS uses Edge TTS (not OpenAI) so it doesn't compete at all.
        if (isNewContent) {
          const newOnlyPlan = { ...plan, unlocks: newlyUnlockedFeatures };
          setTimeout(async () => {
            const parallelTasks = [];
            
            // Stream 1: Life Insights (GPT) — fire and forget in parallel
            if (newlyUnlockedFeatures.includes('life_insights')) {
              parallelTasks.push(
                (async () => {
                  try {
                    console.log('[Payment] Stream 1: Generating Life Insights...');
                    await generatePlanContent(newOnlyPlan);
                    console.log('[Payment] Stream 1 done: Life Insights ready.');
                    localStorage.setItem('ai_generation_in_progress', 'false');
                    preloadAllDomainInsights().catch(() => {});
                  } catch (e) {
                    console.log('[Payment] Life Insights error:', e.message);
                    localStorage.setItem('ai_generation_in_progress', 'false');
                  }
                })()
              );
            }
            
            // Stream 2: Future Reels = Unified Story (GPT) → Sequential TTS (Edge TTS)
            if (newlyUnlockedFeatures.includes('future_reels')) {
              parallelTasks.push(
                (async () => {
                  try {
                    console.log('[Payment] Stream 2: Starting Unified Story + TTS...');
                    const ttsResult = await initializeAfterPayment();
                    console.log('[Payment] Stream 2 done: TTS result =', ttsResult);
                    // Only declare ready if audio was actually generated
                    if (ttsResult) {
                      localStorage.setItem('future_reels_ready', 'true');
                      window.dispatchEvent(new Event('future-reels-ready'));
                    }
                  } catch (e) {
                    console.log('[Payment] TTS init error:', e.message);
                  } finally {
                    localStorage.removeItem('payment_generation_active');
                    localStorage.removeItem('future_reels_generating');
                  }
                })()
              );
            }
            
            // Wait for all parallel streams to complete
            await Promise.allSettled(parallelTasks);
            console.log('[Payment] All generation streams complete.');
          }, 300);
        }
      } else if (!paymentResult?.cancelled) {
        toast.error(sanitizeErrorMessage(paymentResult?.message, 'Payment failed. Please try again.'));
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(sanitizeErrorMessage(error?.message, 'Payment failed. Please try again.'));
      setLoading(false);
      setSelectedPlan(null);
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  // Floating particles - matching login/splash theme
  const particles = [...Array(20)].map((_, i) => ({
    id: i,
    size: Math.random() * 3 + 1,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: 3 + Math.random() * 2,
    delay: Math.random() * 2,
    color: i % 3 === 0 ? '#92FFF7' : i % 3 === 1 ? '#74B3FF' : '#5F42C9'
  }));

  // Loading timeout — show content after 500ms max, never block the UI
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => setLoadingTimedOut(true), 500);
      return () => clearTimeout(timer);
    }
    setLoadingTimedOut(false);
  }, [isLoading]);

  if (isLoading && !loadingTimedOut) {
    // Show loading for max 3 seconds, then show the page anyway
    // This prevents infinite loading if session check is slow
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          style={{ 
            width: 32, 
            height: 32, 
            border: '2px solid #74B3FF', 
            borderTopColor: 'transparent', 
            borderRadius: '50%' 
          }}
        />
      </div>
    );
  }

  return (
    <div 
      data-testid="payment-page"
      style={{
        height: '100vh',
        background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Animated cosmic nebula */}
      <motion.div
        style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(0, 255, 255, 0.08) 0%, rgba(138, 43, 226, 0.05) 50%, transparent 80%)',
          filter: 'blur(80px)',
          left: '50%',
          top: '30%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Floating particles */}
      {particles.map((p) => (
        <motion.div
          key={p.id}
          style={{
            position: 'absolute',
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: p.color,
            left: `${p.x}%`,
            top: `${p.y}%`,
            pointerEvents: 'none',
          }}
          animate={{
            y: [0, -15, 0],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 10, maxWidth: 'min(700px, 95vw)', margin: '0 auto', padding: '24px 16px', flex: 1, overflowY: 'auto' }}>
        
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'rgba(116, 179, 255, 0.1)',
            border: '1px solid rgba(116, 179, 255, 0.2)',
            borderRadius: '10px',
            padding: '8px 14px',
            color: '#E2E8F0',
            fontSize: '13px',
            cursor: 'pointer',
            marginBottom: '32px',
          }}
        >
          <ArrowLeft size={16} />
          Back
        </motion.button>

        {/* Header - Stars Arc Logo */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          style={{ marginBottom: '8px' }}
        >
          <svg 
            width="100%" 
            height="40" 
            viewBox="0 0 200 50" 
            style={{ maxWidth: '140px', display: 'block', margin: '0 auto' }}
          >
            <defs>
              <linearGradient id="payArcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#92FFF7" stopOpacity="0.7" />
                <stop offset="50%" stopColor="#74B3FF" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#5F42C9" stopOpacity="0.7" />
              </linearGradient>
              <filter id="payStarGlow">
                <feGaussianBlur stdDeviation="1.5" result="blur"/>
                <feMerge>
                  <feMergeNode in="blur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            <motion.path
              d="M 30 40 Q 65 16, 100 10 T 170 40"
              stroke="url(#payArcGrad)"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              filter="url(#payStarGlow)"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            />
            
            {[
              { x: 30, y: 40, size: 5, color: '#92FFF7' },
              { x: 65, y: 16, size: 6, color: '#82D9F9' },
              { x: 100, y: 10, size: 7, color: '#74B3FF' },
              { x: 135, y: 16, size: 6, color: '#6A7BE8' },
              { x: 170, y: 40, size: 5, color: '#5F42C9' },
            ].map((star, i) => (
              <motion.path
                key={i}
                d={`
                  M ${star.x} ${star.y - star.size}
                  L ${star.x + star.size * 0.35} ${star.y - star.size * 0.35}
                  L ${star.x + star.size} ${star.y}
                  L ${star.x + star.size * 0.35} ${star.y + star.size * 0.35}
                  L ${star.x} ${star.y + star.size}
                  L ${star.x - star.size * 0.35} ${star.y + star.size * 0.35}
                  L ${star.x - star.size} ${star.y}
                  L ${star.x - star.size * 0.35} ${star.y - star.size * 0.35}
                  Z
                `}
                fill={star.color}
                filter="url(#payStarGlow)"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 + i * 0.05 }}
              />
            ))}
          </svg>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ textAlign: 'center', marginBottom: '32px' }}
        >
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(20px, 5.5vw, 26px)',
            fontWeight: 300,
            color: '#F3E5AB',
            marginBottom: '8px',
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
          }}>
            {headerTitle}
          </h1>
          {currentPlanLevel > 0 && !hasMaxPlan && (
            <p style={{
              fontSize: '12px',
              color: '#74B3FF',
              marginBottom: '4px',
              fontWeight: 500,
            }}>
              Current: {currentPlanName} Plan
            </p>
          )}
          <p style={{
            fontSize: '13px',
            color: 'rgba(226, 232, 240, 0.5)',
            maxWidth: '280px',
            margin: '0 auto',
            lineHeight: 1.5,
          }}>
            {hasMaxPlan ? 'You have access to all features' : headerSubtext}
          </p>
        </motion.div>

        {/* Plans */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '28px' }}>
          {hasMaxPlan ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: 'rgba(116, 179, 255, 0.08)',
                border: '1px solid rgba(116, 179, 255, 0.3)',
                borderRadius: '16px',
                padding: '32px 20px',
                textAlign: 'center',
              }}
            >
              <h3 style={{ color: '#E2E8F0', fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                Ultimate Plan Active
              </h3>
              <p style={{ color: 'rgba(226, 232, 240, 0.5)', fontSize: '13px' }}>
                You have access to all features including Timeline, Future Reels, and unlimited predictions.
              </p>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(-1)}
                style={{
                  marginTop: '20px',
                  padding: '12px 32px',
                  background: 'linear-gradient(135deg, #74B3FF 0%, #5F42C9 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Go Back
              </motion.button>
            </motion.div>
          ) : (
          upgradePlans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              data-testid={`plan-${plan.id}`}
              style={{
                position: 'relative',
                background: plan.popular 
                  ? 'rgba(116, 179, 255, 0.08)'
                  : 'rgba(116, 179, 255, 0.03)',
                border: plan.popular 
                  ? '1px solid rgba(116, 179, 255, 0.3)'
                  : '1px solid rgba(116, 179, 255, 0.1)',
                borderRadius: '16px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onClick={() => !loading && handlePayment(plan)}
            >
              {/* Tag */}
              {plan.tag && (
                <div style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '20px',
                  background: plan.popular 
                    ? 'linear-gradient(135deg, #74B3FF 0%, #5F42C9 100%)'
                    : 'rgba(146, 255, 247, 0.2)',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '10px',
                  fontWeight: 600,
                  color: plan.popular ? '#fff' : '#92FFF7',
                  letterSpacing: '0.5px',
                }}>
                  {plan.tag}
                </div>
              )}

              {/* Plan Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#E2E8F0',
                    marginBottom: '4px',
                  }}>
                    {plan.name}
                  </h3>
                  <p style={{
                    fontSize: '11px',
                    color: 'rgba(226, 232, 240, 0.4)',
                  }}>
                    Monthly • Cancel anytime
                  </p>
                </div>
                
                {/* Price — native platforms show Apple/Google Store localised price,
                    web shows Razorpay backend price. Zero hardcoded labels. */}
                <div style={{ textAlign: 'right' }}>
                  {(() => {
                    const backendPkgId = plan.id.split('_')[0]; // starter_199 → starter
                    const upInfo = upgradePrices[backendPkgId];
                    const isUpgrade = upInfo?.is_upgrade;
                    const livePkg = livePackages[backendPkgId];

                    // For upgrade: still use backend-calculated differential
                    // (stores don't know about upgrade pricing)
                    const displayLabel = isUpgrade
                      ? formatPrice(upInfo.upgrade_price)
                      : getDisplayPrice(backendPkgId);

                    const strikeAmount = isUpgrade
                      ? livePkg?.amount
                      : livePkg?.original_price;

                    // Currency mismatch guard: strikeAmount is always in INR (₹).
                    // displayLabel on native iOS/Android comes from Apple/Google
                    // store and follows the user's App Store region currency
                    // (e.g. "$1.99" if Apple ID is US-region). Showing "₹399 $1.99"
                    // side-by-side looks broken. Detect currency mismatch and hide
                    // strike when display is non-INR.
                    const displayIsINR = !displayLabel ||
                      displayLabel.includes('₹') ||
                      displayLabel.includes('Rs') ||
                      displayLabel.includes('INR');

                    const showStrike = displayIsINR && strikeAmount && (!isUpgrade ? strikeAmount > (livePkg?.amount ?? 0) : true);

                    return (
                      <>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                          {showStrike && (
                            <span style={{
                              fontSize: '12px',
                              color: 'rgba(226, 232, 240, 0.3)',
                              textDecoration: 'line-through',
                            }}>
                              {formatPrice(strikeAmount)}
                            </span>
                          )}
                          <span style={{
                            fontSize: '28px',
                            fontWeight: 700,
                            color: plan.popular ? '#74B3FF' : '#92FFF7',
                          }}>
                            {displayLabel || '—'}
                          </span>
                        </div>
                        {isUpgrade && upInfo.savings > 0 && (
                          <span style={{
                            fontSize: '10px',
                            color: '#22c55e',
                            fontWeight: 600,
                          }}>
                            Save {formatPrice(upInfo.savings)} (upgrade)
                          </span>
                        )}
                      </>
                    );
                  })()}
                  <span style={{
                    fontSize: '11px',
                    color: 'rgba(226, 232, 240, 0.4)',
                    display: 'block',
                  }}>
                    {plan.duration}
                  </span>
                </div>
              </div>

              {/* Features */}
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                marginBottom: '16px',
              }}>
                {plan.features.map((feature, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '11px',
                    color: 'rgba(226, 232, 240, 0.7)',
                  }}>
                    <Check size={12} style={{ color: '#74B3FF', flexShrink: 0 }} />
                    <span style={{ lineHeight: 1.3 }}>{feature}</span>
                  </div>
                ))}
                {plan.notIncluded?.map((feature, idx) => (
                  <div key={`not-${idx}`} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '11px',
                    color: 'rgba(226, 232, 240, 0.25)',
                  }}>
                    <span style={{ width: 12, height: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', flexShrink: 0 }}>✕</span>
                    <span style={{ lineHeight: 1.3, textDecoration: 'line-through' }}>{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <motion.button
                whileTap={{ scale: 0.98 }}
                data-testid={`select-${plan.id}-btn`}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '14px',
                  fontWeight: 600,
                  background: plan.popular 
                    ? 'linear-gradient(135deg, #74B3FF 0%, #5F42C9 100%)'
                    : 'rgba(116, 179, 255, 0.15)',
                  border: plan.popular 
                    ? 'none'
                    : '1px solid rgba(116, 179, 255, 0.3)',
                  borderRadius: '12px',
                  color: '#fff',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: loading && selectedPlan === plan.id ? 0.7 : 1,
                  boxShadow: plan.popular ? '0 4px 20px rgba(116, 179, 255, 0.3)' : 'none',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!loading) handlePayment(plan);
                }}
              >
                {loading && selectedPlan === plan.id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      style={{ 
                        width: 18, 
                        height: 18, 
                        border: '2px solid #fff', 
                        borderTopColor: 'transparent', 
                        borderRadius: '50%' 
                      }}
                    />
                    <span style={{ fontSize: '13px' }}>Opening payment...</span>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const backendPkgId = plan.id.split('_')[0];
                      const upInfo = upgradePrices[backendPkgId];
                      if (upInfo?.is_upgrade) {
                        return `Upgrade for ${formatPrice(upInfo.upgrade_price)}`;
                      }
                      return plan.popular ? 'Get Premium' : `Get ${plan.name}`;
                    })()}
                  </>
                )}
              </motion.button>
            </motion.div>
          ))
          )}
        </div>

        {/* Trust Indicators removed — clean layout */}
        <div style={{ marginBottom: '24px' }} />

        {/* Already Paid? Restore Purchases */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <button
            data-testid="restore-purchases-btn"
            onClick={handleRestore}
            disabled={restoring}
            style={{
              background: 'rgba(237, 193, 97, 0.12)',
              border: '1px solid rgba(237, 193, 97, 0.35)',
              borderRadius: '12px',
              padding: '12px 20px',
              color: '#edc161',
              fontSize: '13px',
              fontWeight: 600,
              cursor: restoring ? 'wait' : 'pointer',
              letterSpacing: '0.3px',
              transition: 'all 200ms ease',
              opacity: restoring ? 0.7 : 1,
            }}
          >
            {restoring ? 'Restoring…' : 'Already Paid? Restore Purchases'}
          </button>
          <div style={{ fontSize: '10px', color: 'rgba(226, 232, 240, 0.4)', marginTop: '8px' }}>
            If you paid but features aren't unlocked, tap to restore.
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '24px', paddingBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '12px' }}>
            <button 
              data-testid="privacy-policy-link"
              onClick={() => navigate('/privacy-policy')} 
              style={{ background: 'none', border: 'none', color: 'rgba(116, 179, 255, 0.6)', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Privacy Policy
            </button>
            <button 
              data-testid="terms-of-service-link"
              onClick={() => navigate('/terms-of-service')} 
              style={{ background: 'none', border: 'none', color: 'rgba(116, 179, 255, 0.6)', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Terms of Service
            </button>
          </div>
          <p style={{
            fontSize: '10px',
            color: 'rgba(226, 232, 240, 0.2)',
            letterSpacing: '2px',
            fontWeight: 600,
          }}>
            TARAANAYA 2026
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPlans;
